<script>
import {
    uiState,
    setQuickToolsSettings as applyQuickToolsSettings,
    selectQuickToolsPanel as applyQuickToolsPanel,
} from "@app/store/uiStore.svelte.js";
import { configureSecondaryToolbar as applySecondaryToolbarConfig } from "@app/lib/toolbar/secondaryToolbarConfig.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import { penpalAllowedOrigins } from "@app/lib/iframe/parentOrigins.js";
import {
    applyThemeCss,
    applyThemeTokens,
    clearCustomTheme,
} from "@app/lib/theme/themeLoader.js";
import {
    modalState,
    showModal,
    hideModal,
} from "@app/store/modalStore.svelte.js";
import { styleState } from "@app/store/styleStore.svelte.js";
import { applyRemoteWatermark } from "@app/store/watermarkStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import { externalDataState } from "@app/store/externalDataStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { fade } from "svelte/transition";
import { projectState } from "@app/store/projectStore.svelte.js";
import {
    updateDuration,
    playerState,
    setPlayerEngine,
} from "@app/store/playerStore.svelte.js";
import { isFlxUrl, parseFlxUrl } from "@app/lib/player/flxUrl.js";
import {
    markerState,
    resetMarkerState,
    resetMarkerStateWithData,
} from "@app/store/markerStore.svelte.js";
import { issueState } from "@app/store/issueStore.svelte.js";
import { speakerState } from "@app/store/speakerStore.svelte.js";
import { v4 as uuidv4 } from "uuid";
import { metadataState } from "@app/store/metadataStore.svelte.js";
import {
    eventGroupState,
    resetEventGroupState,
    isEventGroupLocked,
    isSelectedEventGroupLocked,
} from "@app/store/eventGroupStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import {
    historyState,
    insertHistoryAction,
    establishHistoryBaseline,
    hasUnsavedChanges as checkUnsavedChanges,
} from "@app/store/historyStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import * as m from "@app/paraglide/messages.js";

import _Event from "@cclib/classes/event.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import _Project from "@cclib/classes/project.js";
import _Marker from "@cclib/classes/marker.js";
import _StyleGuide from "@cclib/classes/styleGuide.js";
import loadMedia from "@cclib/functions/projects/loadMedia.js";
import decode from "@cclib/functions/decode.js";
import encode from "@cclib/functions/encode.js";
import defaults from "@cclib/lib/defaults.js";
import formats from "@cclib/lib/formats.js";
import orderByTime from "@cclib/functions/eventGroups/orderByTime.js";
import tcRound from "@cclib/functions/eventGroups/tcRound.js";
import removeHtmlEntities from "@cclib/functions/eventGroups/removeHtmlEntities.js";
import wrapStyledTextWithSpan from "@cclib/functions/quill/wrapStyledTextWithSpan.js";
import bufferToHex from "@cclib/functions/utility/bufferToHex.js";
import fixOverlap from "@cclib/functions/eventGroups/fixOverlap.js";
import checkForOverlaps from "@cclib/functions/eventGroups/checkForOverlaps.js";
import automaticOs from "@cclib/functions/special/automaticOffset.js";
import alignCaptionFile from "@cclib/functions/special/alignCaptionFile.js";
import getAutomaticOffsetAmount from "@cclib/functions/special/getAutomaticOffsetAmount.js";
import colorDict from "@cclib/dict/colors.js";
import stripTags from "@cclib/functions/quill/stripTags.js";
import encodeHtml from "@cclib/functions/eventGroups/encodeHtml.js";
import replaceTrialText from "@cclib/functions/eventGroups/replaceTrialText.js";
import autoFormat from "@cclib/functions/eventGroups/autoFormat.js";
import autoFormatCheck from "@cclib/functions/eventGroups/autoFormatCheck.js";
import correctReadingSpeed from "@cclib/functions/eventGroups/correctReadingSpeed.js";
import tcLib from "@cclib/lib/timecode.js";
import incodeTime from "@cclib/lib/incodeTime.js";
// Import the externalized style guide tests
import { validateEventGroup } from "@cclib/lib/styleGuideTests.js";
import { metricsState } from "@app/store/metricsStore.svelte.js";
import insertEvent from "@cclib/functions/eventGroups/insertEvent.js";
import { WindowMessenger, connect } from "penpal";
import { setParentRemote } from "@app/lib/audio/parentTts.js";
import { ccprjFunc } from "@app/lib/ccprj.js";
import {
    dispatchKeyboardShortcutsUpdated,
    getStoredKeyboardShortcuts,
    mergeKeyboardShortcuts,
    saveKeyboardShortcuts,
} from "@app/lib/keyboardShortcuts.js";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { saveAs } from "file-saver";

const hexColors = [
    "#ff66cc",
    "#00cc00",
    "#0066ff",
    "#6600ff",
    "#ff0066",
    "#6666ff",
    "#00cc99",
    "#99ccff",
    "#ff9966",
    "#ff33cc",
    "#009900",
    "#ff9933",
    "#009999",
    "#333399",
    "#999966",
    "#99ccff",
    "#990033",
    "#996600",
    "#ffcc99",
    "#6699ff",
];

resetProjectState();

let progress = $state(0),
    statusMsg = $state("Initializing..."),
    modalTitle = $state("Importing...");

let eventGroupDefaults =
    JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};

/* Letter track (A-D) helpers - mirror the Subtitle Import/Merge/Export modals */
const TRACK_LETTERS = ["A", "B", "C", "D"];
const ALL_TRACK_OPTIONS = ["none", ...TRACK_LETTERS];

function normalizeLetterTrack(value) {
    return TRACK_LETTERS.includes(value) ? value : false;
}

/* Filter events based on the selected export tracks. All events export when every track is selected (default). */
function filterEventsByTrack(events, selectedTracks) {
    if (
        !Array.isArray(selectedTracks) ||
        selectedTracks.length === 0 ||
        ALL_TRACK_OPTIONS.every((option) => selectedTracks.includes(option))
    ) {
        return events;
    }

    return events.filter((event) => {
        const eventTrack = TRACK_LETTERS.includes(event.track)
            ? event.track
            : "none";
        return selectedTracks.includes(eventTrack);
    });
}

/* Unique letter tracks (A-D) currently assigned to events, in track order */
function usedLetterTracks(events) {
    if (!Array.isArray(events)) return [];
    let used = new Set();
    events.forEach((event) => {
        if (TRACK_LETTERS.includes(event.track)) used.add(event.track);
    });
    return TRACK_LETTERS.filter((letter) => used.has(letter));
}

function getSyncTargetIndex() {
    if (
        projectState.selected === false ||
        projectState.selected === undefined ||
        projectState.selected === null
    ) {
        throw new Error("No event group is selected.");
    }

    let targetIndex = Number(projectState.selected);
    let eventGroup = eventGroupState[targetIndex];

    if (!Number.isInteger(targetIndex) || !eventGroup) {
        throw new Error("Selected event group was not found.");
    }

    if (["audio description", "audio dubbing"].includes(eventGroup.type)) {
        throw new Error("Audio description and audio dubbing event groups cannot be synced.");
    }

    return targetIndex;
}

function assertGroupEditable(index = projectState.selected) {
    if (isEventGroupLocked(index)) {
        throw new Error(m.event_group_locked_toast());
    }
}

async function decodeStoredTranscriptWordMap() {
    if (!externalDataState.transcript) {
        throw new Error("No transcript is loaded.");
    }

    let srcProfile = externalDataState.profile || "deepgram";
    let options = new defaults.options({
        profile: formats.profileMapping[srcProfile]?.name || srcProfile,
        formatOptions: [
            {
                name: "Import Type",
                type: "list",
                values: ["subtitle", "transcription"],
                selected: "word map",
            },
            {
                name: "Max Characters",
                type: "number-input",
                values: "",
                selected: 32,
            },
            {
                name: "Max Lines",
                type: "number-input",
                values: "",
                selected: 3,
            },
        ],
        frameRate: projectState.frameRate,
        dropFrame: projectState.dropFrame,
        window: {
            width: styleState.width,
            height: styleState.height,
            xPadding: styleState.xPadding,
            yPadding: styleState.yPadding,
        },
    });

    return await decode(JSON.stringify(externalDataState.transcript), options);
}

function formatSyncResponse(alignmentRes, message) {
    return {
        success: true,
        message: message,
        drift: alignmentRes?.drift ?? null,
        driftAnalysis: alignmentRes?.driftAnalysis ?? null,
        missingDialogueCount: Array.isArray(alignmentRes?.missingSegments)
            ? alignmentRes.missingSegments.length
            : 0,
    };
}

function formatSyncError(err) {
    return {
        success: false,
        message: err.message,
        drift: null,
        driftAnalysis: null,
        missingDialogueCount: 0,
    };
}

async function runTranscriptAlignment(message = "Transcript alignment completed successfully.") {
    let targetIndex = getSyncTargetIndex();
    let wordMap = await decodeStoredTranscriptWordMap();
    let eventGroup = JSON.parse(JSON.stringify(eventGroupState[targetIndex]));

    if (!eventGroup.events || eventGroup.events.length === 0) {
        throw new Error("Selected event group has no events to sync.");
    }

    if (
        !eventGroup.events.every((event) => {
            return Number.isFinite(event.start) && Number.isFinite(event.end);
        })
    ) {
        throw new Error("Selected event group must have timed events to sync.");
    }

    let alignmentRes = await alignCaptionFile(
        eventGroup,
        wordMap,
        projectState.frameRate,
        true,
    );

    return {
        targetIndex: targetIndex,
        alignmentRes: alignmentRes,
        response: formatSyncResponse(alignmentRes, message),
    };
}

function applyAlignedTimings(targetIndex, alignedEventGroup) {
    assertGroupEditable(targetIndex);

    eventGroupState[targetIndex].events.forEach((event, evIndex, events) => {
        let alignedEvent = alignedEventGroup.events[evIndex];

        if (alignedEvent) {
            events[evIndex].start = alignedEvent.start;
            events[evIndex].end = alignedEvent.end;
        }
    });
}

function normalizeSpeaker(speaker) {
    let name;
    let id;
    let colour;

    if (typeof speaker === "string") {
        name = speaker.trim();
    } else if (speaker && typeof speaker === "object" && !Array.isArray(speaker)) {
        name = typeof speaker.name === "string" ? speaker.name.trim() : "";
        id = speaker.id;
        colour = speaker.colour || speaker.color;
    } else {
        throw new Error("Speaker must be a name string or speaker-like object.");
    }

    if (!name) {
        throw new Error("Speaker name is required.");
    }

    if (typeof id === "string") {
        id = id.trim();
    }

    if (id === undefined || id === null || id === "") {
        id = uuidv4();
    } else if (speakerState.some((existingSpeaker) => existingSpeaker.id === id)) {
        throw new Error("Speaker id already exists.");
    }

    if (typeof colour === "string") {
        colour = colour.trim();
    }

    return {
        id: id,
        name: name,
        colour:
            colour ||
            colorDict.speakerOrder[
                speakerState.length % colorDict.speakerOrder.length
            ],
    };
}

function normalizeEventGroupOptions(eventGroupOptions) {
    if (eventGroupOptions === undefined) {
        eventGroupOptions = {};
    }

    if (
        !eventGroupOptions ||
        typeof eventGroupOptions !== "object" ||
        Array.isArray(eventGroupOptions) ||
        ![Object.prototype, null].includes(Object.getPrototypeOf(eventGroupOptions))
    ) {
        throw new Error("Event group options must be a plain object.");
    }

    let normalizedOptions = { ...eventGroupOptions };

    if (
        normalizedOptions.events !== undefined &&
        !Array.isArray(normalizedOptions.events)
    ) {
        throw new Error("Event group events must be an array.");
    }

    if (normalizedOptions.events !== undefined) {
        normalizedOptions.events = JSON.parse(
            JSON.stringify(normalizedOptions.events),
        );
    }

    return {
        ...eventGroupDefaults,
        type: "subtitle",
        name: "Untitled Event Group",
        linkedGroup: false,
        allowOriginalEdit: false,
        language: eventGroupDefaults.language || "en-US",
        rtl: eventGroupDefaults.rtl ?? false,
        frameRate: projectState.frameRate,
        dropFrame: projectState.dropFrame,
        incode: projectState.incode,
        maxCps: eventGroupDefaults.maxCps ?? 25,
        maxWpm: eventGroupDefaults.maxWpm ?? 350,
        maxChars: eventGroupDefaults.maxChars ?? 32,
        maxLines: eventGroupDefaults.maxLines ?? 2,
        minDuration: eventGroupDefaults.minDuration ?? 0.5,
        maxDuration: eventGroupDefaults.maxDuration ?? 12,
        overlap: eventGroupDefaults.overlap,
        illegalChars: eventGroupDefaults.illegalChars,
        fontFamily: styleState.fontFamily ?? eventGroupDefaults.fontFamily ?? "plexMono",
        fontSize: styleState.fontSize || eventGroupDefaults.fontSize || 16,
        ...normalizedOptions,
    };
}

function setHistoryBaseline(name = "load project") {
    establishHistoryBaseline({ name });
}

/* PenPal Setup */
const messenger = new WindowMessenger({
    remoteWindow: window.parent,
    allowedOrigins: penpalAllowedOrigins(environment.allowedParentOrigins),
});

const connection = connect({
    messenger,
    // Methods the iframe window is exposing to the parent window that should be documented.
    methods: {
        getVersion() {
            return environment.version;
        },
        updateModalStatus(statusInfo) {
            progress = statusInfo.progress;
            statusMsg = statusInfo.msg;
            return true;
        },
        toggleStatusModal(show) {
            if (show) {
                showModal("iFrameRemote");
            } else if (modalState.selected === "iFrameRemote") {
                hideModal();
            } else {
                showModal("iFrameRemote");
            }
        },
        updateModalTitle(title) {
            modalTitle = title;
            return true;
        },
        completeImport() {
            //close modal
            //alert the user using Sweet Alert Library to let them know the import was successful
            Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                icon: "success",
                title: "Success",
                text: "Import completed successfully.",
                confirmButtonText: "OK",
            });
            setTimeout(() => {
                uiState.timeline = true;
                hideModal();
            }, 1000);
        },
        failImport(msg) {
            //close modal
            toast.push("Import failed: " + msg, {
                classes: ["toast-danger"],
            });

            /* Alert the user using Sweet Alert Library */
            Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                icon: "error",
                title: "Error",
                text: "Import failed: " + msg,
                confirmButtonText: "OK",
            });

            hideModal();
        },
        alertUser(alertObj) {
            /* Alert the user using Sweet Alert Library */
            Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: alertObj.title || "Notice",
                text: alertObj.text || "Please review this message.",
                confirmButtonText: "OK",
            });
        },
        setProjectId(id) {
            projectState.id = id;
        },
        setProjectName(name) {
            projectState.name = name;
        },
        setProjectDescription(description) {
            projectState.description = description;
        },
        setProjectFrameRate(frameRate) {
            projectState.frameRate = frameRate;
        },
        setProjectDropFrame(dropFrame) {
            projectState.dropFrame = dropFrame;
        },
        setProjectIncode(incode) {
            projectState.incode = tcLib.tcToSec(
                incode,
                projectState.frameRate,
                projectState.dropFrame,
                true,
            );
        },
        setProjectState(state) {
            // Instead of reassigning projectState directly, update its properties
            Object.keys(state).forEach((key) => {
                if (key !== "selected" || state.selected !== undefined) {
                    projectState[key] = state[key];
                }
            });
        },
        setAdLicense(enabled) {
            // When enabled, Audio Description voice listing and rendering are
            // routed through the parent host's getVoices/renderAudio methods
            // instead of the Firebase cloud text-to-speech path.
            environment.adLicense = !!enabled;
            return environment.adLicense;
        },
        setParentToolbarActions({ exportSubtitle, cancel } = {}) {
            if (exportSubtitle !== undefined) {
                environment.parentExportSubtitle = !!exportSubtitle;
            }
            if (cancel !== undefined) {
                environment.parentCancel = !!cancel;
            }
            return {
                exportSubtitle: environment.parentExportSubtitle,
                cancel: environment.parentCancel,
            };
        },
        configureSecondaryToolbar(settings) {
            try {
                return applySecondaryToolbarConfig(settings);
            } catch (err) {
                console.error("Error configuring secondary toolbar:", err);
                return {
                    success: false,
                    message: err.message,
                };
            }
        },
        enableSubtitleMode(){
            styleState.ccMode = false;
        },
        enableCaptionMode(){
            styleState.ccMode = true;
        },
        setPreviewFontSize(size) {
            styleState.fontSize = size;
        },
        setPreviewFontFamily(family) {
            styleState.fontFamily = family;
        },
        /**
         * Apply structured chrome theme tokens (CSS variables / aliases).
         * Call after connection.promise resolves, before media import when possible.
         * @param {Record<string, string | number | null | undefined>} tokens
         */
        setTheme(tokens) {
            try {
                applyThemeTokens(tokens);
                return { success: true, message: "Theme tokens applied." };
            } catch (error) {
                console.error("setTheme error:", error);
                return {
                    success: false,
                    message: error?.message || "Failed to apply theme tokens.",
                };
            }
        },
        /**
         * Inject raw CSS for advanced host theming of app chrome.
         * @param {string} cssText
         */
        loadCustomStyles(cssText) {
            try {
                applyThemeCss(cssText);
                return { success: true, message: "Custom styles loaded." };
            } catch (error) {
                console.error("loadCustomStyles error:", error);
                return {
                    success: false,
                    message: error?.message || "Failed to load custom styles.",
                };
            }
        },
        /**
         * Clear runtime theme CSS and token overrides.
         * Build-time system theme stylesheet (if any) remains.
         */
        clearTheme() {
            try {
                clearCustomTheme();
                return { success: true, message: "Custom theme cleared." };
            } catch (error) {
                console.error("clearTheme error:", error);
                return {
                    success: false,
                    message: error?.message || "Failed to clear custom theme.",
                };
            }
        },
        /**
         * Apply a visual watermark over the media player.
         * The host resolves the display values, so dateTime/email/ipAddress
         * arrive as strings (or null when that field is off).
         * @param {{enabled: boolean, showDateTime: boolean, dateTime: string|null, email: string|null, ipAddress: string|null, customText: string|null, alignment: "top"|"center"|"bottom", textOpacity: number, shadowOpacity: number}} settings
         */
        setWatermark(settings) {
            try {
                const applied = applyRemoteWatermark(settings);
                return {
                    success: true,
                    message: "Watermark applied.",
                    settings: applied,
                };
            } catch (error) {
                console.error("setWatermark error:", error);
                return {
                    success: false,
                    message: error?.message || "Failed to apply watermark.",
                };
            }
        },
        setDisplaySettings(settings) {
            try {
                // Validate input
                if (!settings || typeof settings !== "object") {
                    throw new Error("Settings object is required.");
                }

                let updated = false;

                // Update display width (1-100 characters)
                if (settings.displayWidth !== undefined) {
                    const width = Number(settings.displayWidth);
                    if (width >= 1 && width <= 100) {
                        styleState.width = width;
                        updated = true;
                    } else {
                        throw new Error(
                            "Display width must be between 1 and 100",
                        );
                    }
                }

                // Update display height (1-50 lines)
                if (settings.displayHeight !== undefined) {
                    const height = Number(settings.displayHeight);
                    if (height >= 1 && height <= 50) {
                        styleState.height = height;
                        updated = true;
                    } else {
                        throw new Error(
                            "Display height must be between 1 and 50",
                        );
                    }
                }

                // Update horizontal padding (0-50%)
                if (settings.xPadding !== undefined) {
                    const xPad = Number(settings.xPadding);
                    if (xPad >= 0 && xPad <= 50) {
                        styleState.xPadding = xPad;
                        updated = true;
                    } else {
                        throw new Error(
                            "X padding must be between 0 and 50.",
                        );
                    }
                }

                // Update vertical padding (0-50%)
                if (settings.yPadding !== undefined) {
                    const yPad = Number(settings.yPadding);
                    if (yPad >= 0 && yPad <= 50) {
                        styleState.yPadding = yPad;
                        updated = true;
                    } else {
                        throw new Error(
                            "Y padding must be between 0 and 50.",
                        );
                    }
                }

                // Update line spacing (0-50%)
                if (settings.lineSpacing !== undefined) {
                    const spacing = Number(settings.lineSpacing);
                    if (spacing >= 0 && spacing <= 50) {
                        styleState.lineSpacing = spacing;
                        updated = true;
                    } else {
                        throw new Error(
                            "Line spacing must be between 0 and 50.",
                        );
                    }
                }

                if (!updated) {
                    return {
                        success: false,
                        message: "No display settings were updated.",
                    };
                }

                // Save to localStorage (matching Display.svelte behavior)
                localStorage.setItem(
                    "cc-display-defaults",
                    JSON.stringify({
                        displayWidth: styleState.width,
                        displayHeight: styleState.height,
                        xPadding: styleState.xPadding,
                        yPadding: styleState.yPadding,
                        lineSpacing: styleState.lineSpacing,
                    }),
                );

                localStorage.setItem(
                    "cc-style-defaults",
                    JSON.stringify(styleState),
                );

                toast.push("Display settings updated.", {
                    classes: ["toast-success"],
                });

                return {
                    success: true,
                    message: "Display settings updated successfully.",
                    settings: {
                        displayWidth: styleState.width,
                        displayHeight: styleState.height,
                        xPadding: styleState.xPadding,
                        yPadding: styleState.yPadding,
                        lineSpacing: styleState.lineSpacing,
                    },
                };
            } catch (err) {
                console.error("Error updating display settings:", err);
                toast.push(
                    "Failed to update display settings: " + err.message,
                    {
                        classes: ["toast-danger"],
                    },
                );
                return {
                    success: false,
                    message: err.message,
                };
            }
        },
        getKeyboardShortcuts() {
            try {
                let keyboardShortcuts = getStoredKeyboardShortcuts(environment.mac);
                let count = keyboardShortcuts.reduce((total, group) => {
                    return total + group.shortcuts.length;
                }, 0);

                return {
                    success: true,
                    data: keyboardShortcuts,
                    count,
                    message: "Keyboard shortcuts retrieved successfully.",
                };
            } catch (err) {
                console.error("Error retrieving keyboard shortcuts:", err);
                return {
                    success: false,
                    data: [],
                    count: 0,
                    message: err.message,
                };
            }
        },
        setKeyboardShortcuts(shortcuts) {
            try {
                let currentShortcuts = getStoredKeyboardShortcuts(environment.mac);
                let mergeResult = mergeKeyboardShortcuts(
                    currentShortcuts,
                    shortcuts,
                );
                let saveResult = saveKeyboardShortcuts(mergeResult.shortcuts);

                if (!saveResult.success) {
                    throw new Error(saveResult.error || "Failed to save keyboard shortcuts");
                }

                dispatchKeyboardShortcutsUpdated(saveResult.shortcuts);

                toast.push("Keyboard shortcuts updated.", {
                    classes: ["toast-success"],
                });

                return {
                    success: true,
                    message: "Keyboard shortcuts updated successfully.",
                    updatedCount: mergeResult.updatedCount,
                    ignoredCount: mergeResult.ignoredCount,
                    shortcuts: saveResult.shortcuts,
                };
            } catch (err) {
                console.error("Error updating keyboard shortcuts:", err);
                toast.push(
                    "Failed to update keyboard shortcuts: " + err.message,
                    {
                        classes: ["toast-danger"],
                    },
                );
                return {
                    success: false,
                    message: err.message,
                    updatedCount: 0,
                    ignoredCount: 0,
                };
            }
        },
        setMediaPeaksPath(path) {
            projectState.media.peaksPath = path;
            projectState.media.useFallback = false;
        },
        setMediaPeaksData(data) {
            projectState.media.peaksData = data;
            projectState.media.useFallback = false;
        },
        useFallbackTimeline() {
            // Deprecated: the legacy fallback (YouTube/Vimeo) timeline no longer
            // exists in the Konva-based timeline. Kept as a no-op so external
            // iframe consumers do not crash. Will be removed in a future release.
            projectState.media.useFallback = true;
        },
        useDefaultTimeline() {
            // Deprecated companion to `useFallbackTimeline`. See above.
            projectState.media.useFallback = false;
        },
        toggleTimeline(enable) {
            if (enable) {
                uiState.timeline = true;
            } else {
                uiState.timeline = !uiState.timeline;
            }
        },
        setQuickToolsSettings(settings) {
            try {
                return applyQuickToolsSettings(settings);
            } catch (err) {
                console.error("Error setting quick tools settings:", err);
                return {
                    success: false,
                    message: err.message,
                };
            }
        },
        selectQuickToolsPanel(panelName) {
            try {
                return applyQuickToolsPanel(panelName);
            } catch (err) {
                console.error("Error selecting quick tools panel:", err);
                return {
                    success: false,
                    message: err.message,
                };
            }
        },
        addSpeaker(speaker) {
            try {
                let newSpeaker = normalizeSpeaker(speaker);
                speakerState.push(newSpeaker);

                insertHistoryAction({
                    name: "add speaker",
                    eventGroup: projectState.selected,
                    snapshots: [
                        {
                            store: "speakerState",
                            value: JSON.stringify(speakerState),
                        },
                    ],
                });

                return {
                    success: true,
                    message: "Speaker added successfully.",
                    speaker: JSON.parse(JSON.stringify(newSpeaker)),
                };
            } catch (err) {
                console.error("Error adding speaker:", err);
                return {
                    success: false,
                    message: err.message,
                };
            }
        },
        addEventGroup(eventGroupOptions) {
            try {
                let normalizedOptions =
                    normalizeEventGroupOptions(eventGroupOptions);
                let newEventGroup = { ...new _EventGroup(normalizedOptions) };
                eventGroupState.push(newEventGroup);
                projectState.selected = eventGroupState.length - 1;

                insertHistoryAction({
                    name: "insert event group",
                    eventGroup: projectState.selected,
                    snapshots: [
                        {
                            store: "eventGroupState",
                            value: JSON.stringify(eventGroupState),
                        },
                    ],
                });

                let eventGroup = JSON.parse(JSON.stringify(newEventGroup));
                let index = projectState.selected;

                return {
                    success: true,
                    message: "Event group added successfully.",
                    id: eventGroup.id,
                    index: index,
                    eventCount: Array.isArray(eventGroup.events)
                        ? eventGroup.events.length
                        : 0,
                    eventGroup: eventGroup,
                };
            } catch (err) {
                console.error("Error adding event group:", err);
                return {
                    success: false,
                    message: err.message,
                };
            }
        },
        getSpeakers() {
            return JSON.parse(JSON.stringify(speakerState));
        },
        getMarkers() {
            // Return a copy of the markerState to avoid direct mutation
            return JSON.parse(JSON.stringify(markerState));
        },
        setMarkers(markers) {
            console.log(markers);
            // Instead of direct assignment, we need to update markerState's properties
            if (markers && typeof markers === "object") {
                // If markers has a lists property, update it
                if (Array.isArray(markers.lists)) {
                    markerState.lists = markers.lists;
                }
                // Update other properties as needed
                if ("selected" in markers) {
                    markerState.selected = markers.selected;
                }
                // Add any other properties that might exist on markers
                Object.keys(markers).forEach((key) => {
                    if (key !== "lists" && key !== "selected") {
                        markerState[key] = markers[key];
                    }
                });
            }
        },
        createMarkerList(markerList) {
            let newMarkerList = {
                id: markerState.lists.length,
                name: markerList.name || "Unknown",
                color:
                    markerList.color ||
                    hexColors[Math.floor(Math.random() * 20)],
                markers: [],
            };

            markerState.lists = [...markerState.lists, newMarkerList];
        },
        insertMarker(marker, markerList = 0) {
            let newMarker = { ...new _Marker(marker) };

            if (!markerState.lists[markerList]) {
                return;
            }

            markerState.lists[markerList].markers = [
                ...markerState.lists[markerList].markers,
                newMarker,
            ];
        },
        setPoster(thumbnailUrl) {
            projectState.media.thumbnail = thumbnailUrl;
            player.poster = thumbnailUrl;
        },
        setTranscript(transcript, profile = "deepgram") {
            externalDataState.transcript = transcript;
            externalDataState.profile = profile || "deepgram";
        },
        async checkSync() {
            try {
                let { response } = await runTranscriptAlignment(
                    "Transcript sync check completed successfully.",
                );

                return response;
            } catch (err) {
                console.error("Error checking transcript sync:", {
                    error: err,
                    selected: projectState.selected,
                    profile: externalDataState.profile,
                });
                return formatSyncError(err);
            }
        },
        async resyncEventGroup() {
            try {
                let { targetIndex, alignmentRes, response } =
                    await runTranscriptAlignment(
                        "Event group resynced successfully.",
                    );

                applyAlignedTimings(targetIndex, alignmentRes.eventGroup);
                projectState.selected = targetIndex;
                insertHistoryAction({
                    name: "iframe automatic sync",
                    eventGroup: targetIndex,
                    snapshots: [
                        {
                            store: "eventGroupState",
                            value: JSON.stringify(eventGroupState),
                        },
                    ],
                });

                return response;
            } catch (err) {
                console.error("Error resyncing event group:", {
                    error: err,
                    selected: projectState.selected,
                    profile: externalDataState.profile,
                });
                return formatSyncError(err);
            }
        },
        async loadStyleGuide(styleGuideData) {
            try {
                // Validate input
                if (!styleGuideData) {
                    throw new Error("Style guide data is required.");
                }

                // Get existing style guides from local storage
                let existingGuides =
                    JSON.parse(localStorage.getItem("cc-style-guides")) || [];

                // Create new style guide using the StyleGuide class
                let newStyleGuide = new _StyleGuide(styleGuideData);

                // Convert to plain object for storage
                let styleGuideObject = JSON.parse(
                    JSON.stringify(newStyleGuide),
                );

                // Check if a guide with this ID already exists
                let existingIndex = existingGuides.findIndex(
                    (guide) => guide.id === newStyleGuide.id,
                );

                if (existingIndex > -1) {
                    // Update existing guide
                    existingGuides[existingIndex] = styleGuideObject;
                    toast.push(
                        "Style guide updated: " + newStyleGuide.name,
                        {
                            classes: ["toast-success"],
                        },
                    );
                } else {
                    // Add new guide
                    existingGuides.push(styleGuideObject);
                    toast.push(
                        "Style guide loaded: " + newStyleGuide.name,
                        {
                            classes: ["toast-success"],
                        },
                    );
                }

                // Save to local storage
                localStorage.setItem(
                    "cc-style-guides",
                    JSON.stringify(existingGuides),
                );

                return {
                    success: true,
                    message: "Style guide loaded successfully.",
                    id: newStyleGuide.id,
                };
            } catch (err) {
                console.error("Error loading style guide:", err);
                toast.push("Failed to load style guide: " + err.message, {
                    classes: ["toast-danger"],
                });
                return {
                    success: false,
                    message: err.message,
                };
            }
        },
        getStyleGuides() {
            try {
                // Get all style guides from local storage
                let styleGuides =
                    JSON.parse(localStorage.getItem("cc-style-guides")) || [];

                return {
                    success: true,
                    data: styleGuides,
                    count: styleGuides.length,
                    message: "Style guides retrieved successfully.",
                };
            } catch (err) {
                console.error("Error retrieving style guides:", err);
                return {
                    success: false,
                    data: [],
                    count: 0,
                    message: err.message,
                };
            }
        },
        qcEventGroup(eventGroupId, styleGuideId) {
            try {
                // Validate inputs
                if (!eventGroupId) {
                    throw new Error("Event group ID is required.");
                }
                if (!styleGuideId) {
                    throw new Error("Style guide ID is required.");
                }

                // Find the event group
                let eventGroupIndex = eventGroupState.findIndex((group) => {
                    return group.id === eventGroupId;
                });

                if (eventGroupIndex === -1) {
                    throw new Error(
                        `Event group with ID "${eventGroupId}" not found`,
                    );
                }

                // Get the style guide from local storage
                let styleGuides =
                    JSON.parse(localStorage.getItem("cc-style-guides")) || [];
                let styleGuide = styleGuides.find(
                    (guide) => guide.id === styleGuideId,
                );

                if (!styleGuide) {
                    throw new Error(
                        `Style guide with ID "${styleGuideId}" not found`,
                    );
                }

                // Order events by time before validation
                let eventGroup = orderByTime(eventGroupState[eventGroupIndex]);

                // Run validation using the same function as StyleGuide.svelte
                let errors = validateEventGroup(eventGroup, styleGuide, {
                    frameRate: projectState.frameRate,
                    dropFrame: projectState.dropFrame,
                    excludeSpaces: metricsState.cpsIncludeSpaces === false,
                });

                return {
                    success: true,
                    errors: errors,
                    errorCount: errors.length,
                    eventGroupId: eventGroupId,
                    eventGroupName: eventGroup.name,
                    styleGuideId: styleGuideId,
                    styleGuideName: styleGuide.name,
                    message: `QC completed with ${errors.length} issues.`,
                };
            } catch (err) {
                console.error("Error running QC on event group:", err);
                return {
                    success: false,
                    errors: [],
                    errorCount: 0,
                    message: err.message,
                };
            }
        },
        getSelectedEventGroupId() {
            try {
                // Check if there's a selected event group
                if (
                    projectState.selected === false ||
                    projectState.selected === undefined
                ) {
                    return {
                        success: false,
                        id: null,
                        index: null,
                        message: "No event group is selected.",
                    };
                }

                // Verify the selected index is valid
                if (
                    projectState.selected < 0 ||
                    projectState.selected >= eventGroupState.length
                ) {
                    return {
                        success: false,
                        id: null,
                        index: projectState.selected,
                        message: "Selected event group index is out of bounds.",
                    };
                }

                // Get the selected event group
                let selectedEventGroup = eventGroupState[projectState.selected];

                return {
                    success: true,
                    id: selectedEventGroup.id,
                    index: projectState.selected,
                    name: selectedEventGroup.name,
                    type: selectedEventGroup.type,
                    eventCount: selectedEventGroup.events.length,
                    language: selectedEventGroup.language || "en-US",
                    tracks: usedLetterTracks(selectedEventGroup.events),
                    message: "Selected event group retrieved successfully.",
                };
            } catch (err) {
                console.error("Error getting selected event group ID:", err);
                return {
                    success: false,
                    id: null,
                    index: null,
                    message: err.message,
                };
            }
        },
        async loadTranscript() {
            try {
                let maxChars = eventGroupDefaults.maxChars || 32;
                let maxLines = eventGroupDefaults.maxLines || 2;

                // Determine the source profile
                let srcProfile = externalDataState.profile;

                let options = new defaults.options({
                    profile:
                        formats.profileMapping[srcProfile]?.name || srcProfile,
                    formatOptions: [
                        {
                            name: "Import Type",
                            type: "list",
                            values: ["subtitle", "transcription"],
                            selected: "subtitle",
                        },
                        {
                            name: "Max Characters",
                            type: "number-input",
                            values: "",
                            selected: maxChars,
                        },
                        {
                            name: "Max Lines",
                            type: "number-input",
                            values: "",
                            selected: maxLines,
                        },
                    ],
                    frameRate: projectState.frameRate,
                    dropFrame: projectState.dropFrame,
                    window: {
                        width: styleState.width,
                        height: styleState.height,
                        xPadding: styleState.xPadding,
                        yPadding: styleState.yPadding,
                    },
                });

                let transcriptEventGroup = await decode(
                    JSON.stringify(externalDataState.transcript),
                    options,
                );

                transcriptEventGroup = encodeHtml(transcriptEventGroup);

                transcriptEventGroup.type = "subtitle";
                transcriptEventGroup.name = "Transcript";
                transcriptEventGroup.maxCps = eventGroupDefaults.maxCps || 9999;
                transcriptEventGroup.maxWpm = eventGroupDefaults.maxWpm || 9999;
                transcriptEventGroup.maxChars =
                    eventGroupDefaults.maxChars || 9999;
                transcriptEventGroup.maxLines =
                    eventGroupDefaults.maxLines || 9999;
                transcriptEventGroup.minDuration =
                    eventGroupDefaults.minDuration || 0.5;
                transcriptEventGroup.maxDuration =
                    eventGroupDefaults.maxDuration || 12;
                transcriptEventGroup.overlap = eventGroupDefaults.overlap;
                transcriptEventGroup.illegalChars =
                    eventGroupDefaults.illegalChars;

                // Add speakers to the speaker state
                if (
                    transcriptEventGroup.speakers &&
                    transcriptEventGroup.speakers.length > 0
                ) {
                    // Replace direct reassignment with a spread operation to maintain reactivity
                    speakerState.concat(transcriptEventGroup.speakers);
                }

                // Add the event group to the state
                eventGroupState.push(transcriptEventGroup);
                projectState.selected = eventGroupState.length - 1;

                toast.push("Transcript imported.", {
                    classes: ["toast-success"],
                });

                return {
                    success: true,
                    message: "Transcript imported successfully.",
                };
            } catch (err) {
                console.error("Error importing transcript:", err);
                toast.push("Failed to import transcript: " + err.message, {
                    classes: ["toast-danger"],
                });
                return { success: false, message: err.message };
            }
        },
        async importMedia(
            mediaUrl,
            thumbnailUrl,
            mediaType = "video/mp4",
            storageType = "Cloud Storage",
        ) {
            try {
                await updateStatus({
                    progress: progress,
                    msg: "Loading media...",
                });

                // Framelight X / Grass Valley embed URL → switch to GV player shell
                if (isFlxUrl(mediaUrl)) {
                    const flx = parseFlxUrl(mediaUrl);
                    const updatedState = loadMedia(projectState, {
                        projectName: projectState.name || "Untitled Project",
                        storage: "Framelight",
                        mediaUrl: mediaUrl,
                        thumbnail: thumbnailUrl,
                        incode: 0,
                        frameRate: projectState.frameRate || 24,
                        dropFrame: projectState.dropFrame || false,
                        aspectRatio: "16/9",
                    });

                    Object.keys(updatedState).forEach((key) => {
                        if (
                            key !== "selected" ||
                            updatedState.selected !== undefined
                        ) {
                            projectState[key] = updatedState[key];
                        }
                    });

                    setPlayerEngine("framelight", {
                        embedUrl: flx.embedUrl,
                        playerBaseUrl: flx.playerBaseUrl,
                        materialId: flx.materialId,
                    });

                    // If GvMediaPlayer already mounted, update material / metadata in place
                    if (window.player?.setEmbedUrl) {
                        window.player.setEmbedUrl(
                            flx.embedUrl,
                            flx.materialId,
                            flx.playerBaseUrl,
                        );
                    }
                    if (
                        window.player?.loadMaterial &&
                        flx.materialId &&
                        window.player.playerBaseUrl === flx.playerBaseUrl
                    ) {
                        window.player.loadMaterial(flx.materialId);
                    }
                    if (thumbnailUrl && window.player) {
                        window.player.poster = thumbnailUrl;
                    }

                    return {
                        success: true,
                        message: "Framelight media loaded successfully.",
                    };
                }

                // Non-FLX: ensure Video.js engine is active
                const switchingFromFlx =
                    playerState.engine === "framelight";
                if (switchingFromFlx) {
                    setPlayerEngine("videojs");
                    // Wait for MediaPlayer to remount and publish window.player
                    await new Promise((resolve) => setTimeout(resolve, 150));
                }

                if (window.player) {
                    player.src = "";
                }

                // Instead of direct assignment, we'll update projectState properties
                const updatedState = loadMedia(projectState, {
                    projectName: projectState.name || "Untitled Project",
                    storage: storageType,
                    mediaUrl: mediaUrl,
                    thumbnail: thumbnailUrl,
                    incode: 0,
                    frameRate: projectState.frameRate || 24,
                    dropFrame: projectState.dropFrame || false,
                    aspectRatio: "16/9",
                });

                // Update projectState properties instead of direct assignment
                Object.keys(updatedState).forEach((key) => {
                    if (
                        key !== "selected" ||
                        updatedState.selected !== undefined
                    ) {
                        projectState[key] = updatedState[key];
                    }
                });

                // console.log("Project Media State:", projectState.media);
                if (thumbnailUrl && window.player) {
                    player.poster = thumbnailUrl;
                }

                if (!window.player) {
                    return {
                        success: false,
                        message: "Media player is not ready.",
                    };
                }

                switch (storageType) {
                    case "Vimeo":
                        player.src = projectState.media.path;
                        break;

                    case "YouTube":
                        player.src = projectState.media.path;
                        break;

                    case "HLS Manifest":
                    case "Mux":
                    case "DASH Manifest":
                    case "Cloudflare Stream":
                        player.src = projectState.media.path;
                        break;
                    default:
                        /* Cloud Storage */
                        let actualMediaType = mediaType;
                        if (mediaType === "video/quicktime") {
                            actualMediaType = "video/mp4";
                        } else if (mediaType === "video/x-matroska") {
                            actualMediaType = "video/mkv";
                        }

                        player.src = {
                            src: projectState.media.path,
                            type: actualMediaType,
                        };
                        break;
                }

                return { success: true, message: "Media loaded successfully." };
            } catch (err) {
                console.error("Error loading media:", err);
                return { success: false, message: err.message };
            }
        },
        async importSubtitle(
            subtitleUrl,
            profile,
            decodeOptions = null,
            evgOptions = {},
            autoFormatOptions = {},
            forced = false,
            target = null, // { eventGroupId?, importOption?: "merge"|"replace", track?: "A"|"B"|"C"|"D" }
        ) {
            try {
                // Extract file extension from URL
                const urlParts = subtitleUrl.split(".");
                const srcExt = urlParts[urlParts.length - 1].toLowerCase();
                // Determine profile if not provided
                if (!profile && !srcExt) {
                    console.log(
                        "Error: No profile or file extension provided.",
                    );
                    return {
                        success: false,
                        message: "No subtitle profile or file extension provided.",
                    };
                }

                let srcProfile =
                    profile || formats.getSrcProfileByExtension(srcExt);
                let srcProfileDisplayName =
                    formats.getProfileTitleByName(srcProfile);

                if (!srcProfile) {
                    throw new Error(
                        `Unsupported subtitle format: ${srcExt}`,
                    );
                }

                // Fetch the subtitle file
                const response = await fetch(subtitleUrl);
                if (!response.ok) {
                    throw new Error(
                        `Failed to fetch subtitle file: ${response.statusText}`,
                    );
                }

                let fileContents;
                if (formats.binaryFormats.includes(srcProfile)) {
                    const buffer = await response.arrayBuffer();
                    fileContents = bufferToHex(buffer);
                } else {
                    fileContents = await response.text();
                }

                await updateStatus({
                    progress: 30,
                    msg: "Processing subtitle file...",
                });

                /* Resolve the destination: an existing event group (merge/replace) or a new group */
                let targetEventGroupId =
                    target && typeof target === "object"
                        ? target.eventGroupId
                        : null;
                let importOption = "replace";
                let mergeTrack = false;

                if (targetEventGroupId) {
                    let targetGroupIndex = eventGroupState.findIndex(
                        (group) => group.id === targetEventGroupId,
                    );

                    if (targetGroupIndex === -1) {
                        throw new Error(
                            `Event group with ID "${targetEventGroupId}" not found`,
                        );
                    }

                    if (isEventGroupLocked(targetGroupIndex)) {
                        throw new Error(m.event_group_locked_toast());
                    }

                    importOption =
                        target.importOption === "merge" ? "merge" : "replace";
                    mergeTrack = normalizeLetterTrack(target.track);
                    projectState.selected = targetGroupIndex;
                } else {
                    let eventGroupOptions = {
                        type: evgOptions?.type || "subtitle",
                        name: evgOptions?.name || "Untitled Event Group",
                        linkedGroup: evgOptions?.linkedGroup ?? false,
                        allowOriginalEdit: evgOptions?.allowOriginalEdit ?? false,
                        language: evgOptions?.language || "en-US",
                        rtl: evgOptions?.rtl || false,
                        maxCps: evgOptions?.maxCps || 9999,
                        maxWpm: evgOptions?.maxWpm || 9999,
                        maxChars: evgOptions?.maxChars || 9999,
                        maxLines: evgOptions?.maxLines || 9999,
                        minDuration: evgOptions?.minDuration || 0.5,
                        maxDuration: evgOptions?.maxDuration || 12,
                        overlap: evgOptions?.overlap,
                        illegalChars: evgOptions?.illegalChars,
                        fontFamily: evgOptions?.fontFamily ?? "plexMono",
                        fontSize: evgOptions?.fontSize || 16,
                    };

                    eventGroupState.push({ ...new _EventGroup(eventGroupOptions) });
                    projectState.selected = eventGroupState.length - 1;
                }

                // Set up decoding options
                let options = new defaults.options({
                    profile: srcProfile,
                    formatOptions: decodeOptions
                        ? decodeOptions
                        : formats.profileMapping[srcProfileDisplayName].options
                              ?.decode || [],
                    frameRate: projectState.frameRate,
                    dropFrame: projectState.dropFrame,
                    target_profile: "closedCaptionProject",
                    source_profile: srcProfile,
                    incode: projectState.incode > 0
                        ? "auto"
                        : tcLib.secToTc(
                            projectState.incode,
                            projectState.frameRate,
                            projectState.dropFrame,
                        ),
                    window: {
                        width: styleState.width,
                        height: styleState.height,
                        xPadding: styleState.xPadding,
                        yPadding: styleState.yPadding,
                    },
                });

                // Decode the subtitle file
                let decodedEvGroup = await decode(fileContents, options);
                decodedEvGroup = encodeHtml(decodedEvGroup);

                // Check for automatic offset
                let offsetAmount = getAutomaticOffsetAmount(
                    decodedEvGroup,
                    projectState.frameRate,
                    projectState.dropFrame,
                    tcLib.secToTc(
                        projectState.incode,
                        projectState.frameRate,
                        projectState.dropFrame,
                    ),
                );

                if (offsetAmount != 0 && projectState.incode > 0) {
                    decodedEvGroup = automaticOs(
                        decodedEvGroup,
                        projectState.frameRate,
                        projectState.dropFrame,
                    );
                }

                // Filter out empty events for Pop-On style
                if (
                    decodedEvGroup.events.every(
                        (ev) =>
                            ev.style === "Pop-On" || ev.style === "Paint-On",
                    )
                ) {
                    decodedEvGroup.events = decodedEvGroup.events.filter(
                        (event) => {
                            return (
                                stripTags(event.text) ||
                                event.notes ||
                                event.reply
                            );
                        },
                    );

                    // Fix overlaps if present
                    if (checkForOverlaps(decodedEvGroup)) {
                        decodedEvGroup = fixOverlap(decodedEvGroup);
                    }
                }

                // Import speakers from events
                decodedEvGroup.events.forEach((event) => {
                    event.speakers.forEach((speaker) => {
                        if (
                            !speakerState.find((s) => s.name === speaker.name)
                        ) {
                            speakerState.push(speaker);
                        }
                    });
                });

                if (forced){
                    //Make forced property of each event true
                    decodedEvGroup.events.forEach((event) => {
                        event.forced = true;
                    });
                }

                // Update the event group (merge appends, otherwise replace)
                let isMergeImport =
                    targetEventGroupId !== null &&
                    targetEventGroupId !== undefined &&
                    importOption === "merge";
                let firstImportedIndex = 0;
                let importedCount = decodedEvGroup.events.length;

                if (isMergeImport) {
                    // Optionally assign imported events to a track when merging with an existing, non-multiview group
                    if (
                        mergeTrack &&
                        eventGroupState[projectState.selected].type !==
                            "multiview"
                    ) {
                        decodedEvGroup.events.forEach((event) => {
                            event.track = mergeTrack;
                        });
                    }

                    firstImportedIndex =
                        eventGroupState[projectState.selected].events.length;
                    eventGroupState[projectState.selected].events = [
                        ...eventGroupState[projectState.selected].events,
                        ...decodedEvGroup.events,
                    ];
                } else {
                    eventGroupState[projectState.selected].events =
                        decodedEvGroup.events;
                }

                if (autoFormatOptions && autoFormatOptions.enable) {
                    let selectedEvents = null;
                    // Restrict formatting to the newly appended events on merge so existing events are untouched
                    if (autoFormatOptions.selective || isMergeImport){
                        selectedEvents = [];
                        eventGroupState[projectState.selected].events.forEach((event, index) => {
                            if (isMergeImport && index < firstImportedIndex) {
                                return;
                            }
                            if (!autoFormatOptions.selective || autoFormatCheck(event, autoFormatOptions.maxLines || 2, autoFormatOptions.maxChars || 32)) {
                                selectedEvents.push(index);
                            }
                        });
                    }
                    eventGroupState[projectState.selected] = autoFormat(
                        eventGroupState[projectState.selected],
                        autoFormatOptions.maxLines || 2,
                        autoFormatOptions.maxChars || 32,
                        autoFormatOptions.minDuration || 0.5,
                        autoFormatOptions.allowOrphanWords ?? true,
                        selectedEvents,
                    );
                }

                toast.push("Subtitle imported.", {
                    classes: ["toast-success"],
                });

                return {
                    success: true,
                    message: "Subtitle imported successfully.",
                    id: eventGroupState[projectState.selected].id,
                    index: projectState.selected,
                    importedCount: importedCount,
                };
            } catch (err) {
                console.error("Error importing subtitle file:", err);
                toast.push("Failed to import subtitle: " + err.message, {
                    classes: ["toast-danger"],
                });
                return { success: false, message: err.message };
            }
        },
        //forcedOption can be include, exclude, and only.
        async exportSubtitle(
            profile,
            encodeOptions,
            saveAsFile = false,
            forcedOption = "include",
            eventGroupId = null,
            exportTracks = ["none", ...TRACK_LETTERS],
        ) {
            try {
                const validTypes = [
                    "subtitle",
                    "translation",
                    "audio description",
                    "audio dubbing",
                    "transcription",
                ];

                if (eventGroupId) {
                    let providedGroup = eventGroupState.find(
                        (group) => group.id === eventGroupId,
                    );
                    if (!providedGroup) {
                        throw new Error(
                            `Event group with ID "${eventGroupId}" not found`,
                        );
                    }
                    if (validTypes.indexOf(providedGroup.type) === -1) {
                        throw new Error(
                            `Event group with ID "${eventGroupId}" has type "${providedGroup.type}" which is not valid for export.`,
                        );
                    }
                } else {
                    eventGroupId = getSelectedEventGroup(validTypes);
                    if (!eventGroupId) {
                        throw new Error(
                            "No valid event group selected for export.",
                        );
                    }
                }

                let eventGroupIndex = eventGroupState.findIndex((group) => {
                    return group.id === eventGroupId;
                });

                if (eventGroupIndex === -1) {
                    throw new Error("Selected event group was not found.");
                }

                let profileName = formats.getProfileTitleByName(profile);
                let profileExt = formats.defaultExtMap[profile];

                let fileName =
                    eventGroupState[eventGroupIndex].name +
                    "." +
                    (profileExt || "srt");
                let copyOfEventGroup = JSON.parse(
                    JSON.stringify(eventGroupState[eventGroupIndex]),
                );

                if (forcedOption === "only") {
                    copyOfEventGroup.events = copyOfEventGroup.events.filter(
                        (event) => event.forced === true,
                    );
                } else if (forcedOption === "exclude") {
                    copyOfEventGroup.events = copyOfEventGroup.events.filter(
                        (event) => event.forced !== true,
                    );
                }

                // Filter by letter track (A-D / "none"). All tracks selected (default) exports everything.
                copyOfEventGroup.events = filterEventsByTrack(
                    copyOfEventGroup.events,
                    exportTracks,
                );

                // Parse profile information
                let frameRate = projectState.frameRate;
                let dropFrame = projectState.dropFrame;

                let useClockTime =
                    formats.clockTimeFormats.indexOf(profileName) > -1
                        ? false
                        : true;

                let trgIncode = incodeTime.mediaSecondsToIncodeLabel(
                    projectState.incode,
                    frameRate,
                    dropFrame,
                );

                let incodeSec = incodeTime.incodeLabelToMediaSeconds(
                    trgIncode,
                    frameRate,
                    dropFrame,
                );

                let incode = useClockTime
                    ? tcLib.secToTcClock(incodeSec, frameRate, dropFrame)
                    : trgIncode;

                // Sort and prepare event group
                copyOfEventGroup = orderByTime(copyOfEventGroup);
                copyOfEventGroup = removeHtmlEntities(copyOfEventGroup);

                // Apply text formatting for trial mode
                if (
                    authState.status === "in_trial" ||
                    environment.system === "demo"
                ) {
                    copyOfEventGroup = replaceTrialText(copyOfEventGroup);
                }

                // Create encode options
                let options = new defaults.options({
                    profile: profile,
                    formatOptions:
                        encodeOptions ||
                        formats.profileMapping[profileName]?.options?.encode ||
                        [],
                    frameRate: frameRate,
                    dropFrame: dropFrame,
                    target_profile: profile,
                    source_profile: "closedCaptionProject",
                    incode: incode || "00:00:00:00",
                    window: {
                        width: styleState.width,
                        height: styleState.height,
                        xPadding: styleState.xPadding,
                        yPadding: styleState.yPadding,
                    },
                });

                // Apply styling to text
                copyOfEventGroup.events.forEach((event, index, events) => {
                    events[index].text = wrapStyledTextWithSpan(event.text);
                });

                // Encode to target format
                let output = await encode(copyOfEventGroup, options);

                // Determine if file should be saved or returned
                if (saveAsFile) {
                    let defaultEncoding =
                        formats.defaultEncodingMap[
                            formats.profileMapping[profileName]?.name
                        ] || "utf-8";
                    let fileBlob;

                    if (defaultEncoding === "buffer") {
                        if (profile.ext === "xlsx") {
                            fileBlob = new Blob([output], {
                                type: "application/octet-stream",
                            });
                        } else {
                            fileBlob = output;
                        }
                    } else if (defaultEncoding.toLowerCase() === "hex") {
                        let byteArray = new Uint8Array(
                            output.match(/.{2}/g).map((e) => parseInt(e, 16)),
                        );
                        fileBlob = new Blob([byteArray], {
                            type: "application/octet-stream",
                        });
                    } else if (defaultEncoding.toLowerCase() === "utf-16le") {
                        fileBlob = new Blob([output], {
                            type: "text/plain;charset=utf-16le",
                        });
                    } else {
                        fileBlob = new Blob([output], {
                            type:
                                "text/plain;charset=" +
                                (profile.encoding || defaultEncoding),
                        });
                    }

                    saveAs(fileBlob, fileName, {
                        autoBom: true,
                    });

                    return {
                        success: true,
                        message: "Subtitle exported and saved.",
                    };
                } else {
                    // Return the output data
                    return {
                        success: true,
                        data: output,
                        fileName: fileName,
                        message: "Subtitle exported successfully.",
                    };
                }
            } catch (err) {
                console.error("Error exporting subtitle:", err);
                toast.push("Failed to export subtitle: " + err.message, {
                    classes: ["toast-danger"],
                });
                return { success: false, message: err.message };
            }
        },
        getEventGroupInfo(){
            return eventGroupState.map((group) => {
                return {
                    id: group.id,
                    name: group.name,
                    type: group.type,
                    eventCount: group.events.length,
                    language: group.language || "en-US",
                    tracks: usedLetterTracks(group.events),
                };
            });
        },
        removeEventGroup(eventGroupId) {
            let index = eventGroupState.findIndex((group) => {
                return group.id === eventGroupId;
            });

            if (index !== -1) {
                if (isEventGroupLocked(index)) {
                    return {
                        success: false,
                        message: m.event_group_locked_toast(),
                    };
                }

                projectState.selected = false;
                eventGroupState.splice(index, 1);    

                if (eventGroupState.length > 0){
                    projectState.selected = 0;
                }
            }
        },
        mergeEventGroups(sourceGroups, eventGroupOptions, orderByStart = true, groupTracks = {}) {
            try {
                if (!Array.isArray(sourceGroups)) {
                    throw new Error("sourceGroups must be an array of event group IDs.");
                }

                let uniqueGroupIds = [...new Set(sourceGroups)].filter((groupId) => {
                    return groupId !== undefined && groupId !== null && groupId !== "";
                });

                if (uniqueGroupIds.length < 2) {
                    throw new Error("At least 2 event group IDs are required to merge.");
                }

                let sourceEventGroups = uniqueGroupIds.map((groupId) => {
                    return eventGroupState.find((group) => group.id === groupId);
                }).filter(Boolean);

                if (sourceEventGroups.length < 2) {
                    throw new Error("At least 2 valid event groups are required to merge.");
                }

                if (sourceEventGroups.some((group) => group.locked)) {
                    throw new Error(m.event_group_locked_toast());
                }

                let normalizedGroupTracks =
                    groupTracks &&
                    typeof groupTracks === "object" &&
                    !Array.isArray(groupTracks)
                        ? groupTracks
                        : {};

                let mergedEvents = [];

                sourceEventGroups.forEach((group) => {
                    if (Array.isArray(group.events) && group.events.length > 0) {
                        let groupEvents = JSON.parse(
                            JSON.stringify(group.events),
                        );

                        // Optionally stamp a letter track on every event copied from this source group
                        let assignedTrack = normalizeLetterTrack(
                            normalizedGroupTracks[group.id],
                        );
                        if (assignedTrack) {
                            groupEvents.forEach((event) => {
                                event.track = assignedTrack;
                            });
                        }

                        mergedEvents = [...mergedEvents, ...groupEvents];
                    }
                });

                if (orderByStart === true) {
                    mergedEvents.sort((eventA, eventB) => {
                        let eventAStart = typeof eventA.start === "number" ? eventA.start : Infinity;
                        let eventBStart = typeof eventB.start === "number" ? eventB.start : Infinity;
                        return eventAStart - eventBStart;
                    });
                }

                mergedEvents = JSON.parse(JSON.stringify(mergedEvents));
                mergedEvents = mergedEvents.map((event) => {
                    return { ...new _Event(event) };
                });

                let normalizedOptions =
                    eventGroupOptions &&
                    typeof eventGroupOptions === "object" &&
                    !Array.isArray(eventGroupOptions)
                        ? eventGroupOptions
                        : {};

                let mergedEventGroupOptions = {
                    type: "subtitle",
                    name: normalizedOptions.name || "Merged Event Group",
                    linkedGroup: normalizedOptions.linkedGroup ?? false,
                    allowOriginalEdit: normalizedOptions.allowOriginalEdit ?? false,
                    language: normalizedOptions.language || eventGroupDefaults.language || "en-US",
                    rtl: normalizedOptions.rtl ?? eventGroupDefaults.rtl ?? false,
                    maxCps: normalizedOptions.maxCps ?? eventGroupDefaults.maxCps ?? 9999,
                    maxWpm: normalizedOptions.maxWpm ?? eventGroupDefaults.maxWpm ?? 9999,
                    maxChars: normalizedOptions.maxChars ?? eventGroupDefaults.maxChars ?? 9999,
                    maxLines: normalizedOptions.maxLines ?? eventGroupDefaults.maxLines ?? 9999,
                    minDuration: normalizedOptions.minDuration ?? eventGroupDefaults.minDuration ?? 0.5,
                    maxDuration: normalizedOptions.maxDuration ?? eventGroupDefaults.maxDuration ?? 12,
                    overlap: normalizedOptions.overlap ?? eventGroupDefaults.overlap,
                    illegalChars: normalizedOptions.illegalChars ?? eventGroupDefaults.illegalChars,
                    fontFamily: normalizedOptions.fontFamily ?? "plexMono",
                    fontSize: normalizedOptions.fontSize || 16,
                    ...normalizedOptions,
                    events: mergedEvents,
                };

                eventGroupState.push({ ...new _EventGroup(mergedEventGroupOptions) });

                let mergedEventGroupIndex = eventGroupState.length - 1;
                projectState.selected = mergedEventGroupIndex;

                eventGroupState[mergedEventGroupIndex].selected =
                    eventGroupState[mergedEventGroupIndex].events.length > 0 ? [0] : [];

                toast.push("Merging Event Groups Complete", {
                    classes: ["toast-success"],
                });

                return {
                    success: true,
                    id: eventGroupState[mergedEventGroupIndex].id,
                    index: mergedEventGroupIndex,
                    eventCount: eventGroupState[mergedEventGroupIndex].events.length,
                    sourceCount: sourceEventGroups.length,
                    message: "Merging Event Groups Complete",
                };
            } catch (err) {
                console.error("Error merging event groups:", err);
                toast.push("Error merging Event Groups. " + err.message, {
                    classes: ["toast-danger"],
                });
                return {
                    success: false,
                    message: err.message,
                };
            }
        },
        insertBlankFrames(
            evIndex = 0,
            frames,
            minFrameGap,
            maxFrameGap,
            shotChangeAware,
        ) {
            try {
                if (!eventGroupState[evIndex]) {
                    throw new Error(
                        `Invalid event group index: ${evIndex}`,
                    );
                }

                assertGroupEditable(evIndex);

                toast.push("Inserting blank frames...", {
                    classes: ["toast-info"],
                });

                let frameGapSec = frames / projectState.frameRate;
                let minFrameGapSec = minFrameGap / projectState.frameRate;
                let maxFrameGapSec = maxFrameGap / projectState.frameRate;

                eventGroupState[evIndex] = orderByTime(
                    eventGroupState[evIndex],
                );
                eventGroupState[evIndex] = tcRound(
                    eventGroupState[evIndex],
                    projectState.frameRate,
                    projectState.dropFrame,
                );

                eventGroupState[evIndex].events.forEach(
                    (event, index, events) => {
                        if (index === events.length - 1) {
                            return;
                        }

                        if (!event.end || event.end < 0) {
                            return;
                        }

                        let eventFrameGap = events[index + 1].start - event.end;
                        if (
                            eventFrameGap <= maxFrameGapSec &&
                            eventFrameGap >= minFrameGapSec
                        ) {
                            //console.log("Event Frame Gap: ",eventFrameGap);
                            if (shotChangeAware) {
                                //console.log("Shot Change Aware is true");
                                let marker = markerState.lists[0].markers.find(
                                    (mark) => {
                                        return (
                                            parseFloat(mark.time).toFixed(5) ===
                                                parseFloat(
                                                    events[index].end,
                                                ).toFixed(5) ||
                                            parseFloat(mark.time).toFixed(5) ===
                                                parseFloat(
                                                    events[index + 1].start,
                                                ).toFixed(5)
                                        );
                                    },
                                );

                                //console.log("Marker Found:", marker);

                                if (marker) {
                                    if (
                                        parseFloat(marker.time).toFixed(5) ===
                                        parseFloat(
                                            events[index + 1].start,
                                        ).toFixed(5)
                                    ) {
                                        if (
                                            events[index + 1].start -
                                                frameGapSec <=
                                            event.start
                                        ) {
                                            events[index + 1].start =
                                                event.end + frameGapSec;
                                        } else {
                                            events[index].end =
                                                events[index + 1].start -
                                                frameGapSec;
                                        }
                                    } else {
                                        if (
                                            events[index].end + frameGapSec >=
                                            events[index + 1].end
                                        ) {
                                            events[index].end =
                                                events[index + 1].start -
                                                frameGapSec;
                                        } else {
                                            events[index + 1].start =
                                                event.end + frameGapSec;
                                        }
                                    }
                                } else {
                                    //console.log("No marker found:");
                                    if (
                                        events[index + 1].start - frameGapSec <=
                                        event.start
                                    ) {
                                        events[index + 1].start =
                                            event.end + frameGapSec;
                                    } else {
                                        events[index].end =
                                            events[index + 1].start -
                                            frameGapSec;
                                    }
                                }
                            } else {
                                if (
                                    events[index + 1].start - frameGapSec <=
                                    event.start
                                ) {
                                    events[index + 1].start =
                                        event.end + frameGapSec;
                                } else {
                                    events[index].end =
                                        events[index + 1].start - frameGapSec;
                                }
                            }
                        }
                    },
                );

                eventGroupState[evIndex] = eventGroupState[evIndex];

                toast.push("Blank frames inserted successfully.", {
                    classes: ["toast-success"],
                });
            } catch (err) {
                console.error(err, err.message);
                toast.push("Failed to insert blank frames.", {
                    classes: ["toast-danger"],
                });
            }
        },
        autoFormat(
            evgIndex,
            maxLines,
            maxChars,
            minDuration,
            allowOrphanWords,
            selective = false,
        ) {
            if (isEventGroupLocked(evgIndex)) {
                toast.push(m.event_group_locked_toast(), {
                    classes: ["toast-warning"],
                });
                return { success: false, message: m.event_group_locked_toast() };
            }

            let selectedEvents = null;
            if (selective){
                selectedEvents = [];
                eventGroupState[evgIndex].events.forEach((event, index) => {
                    if (autoFormatCheck(event, maxLines || 2, maxChars || 32)) {
                        selectedEvents.push(index);
                    }
                });
            }
            eventGroupState[evgIndex] = autoFormat(
                eventGroupState[evgIndex],
                maxLines || 2,
                maxChars || 32,
                minDuration || 0.5,
                allowOrphanWords ?? true,
                selectedEvents,
            );
        },
        autoCorrectReadingSpeed(maxCps, minDuration, maxDuration, minFrameGap, allowMerge) {
            try {
                maxCps = maxCps || 25;
                minDuration = minDuration || 0.5;
                maxDuration = maxDuration || 10;
                minFrameGap = minFrameGap ?? editorState.minFrameGap ?? 0;
                allowMerge = allowMerge ?? true;

                if (isSelectedEventGroupLocked()) {
                    throw new Error(m.event_group_locked_toast());
                }

                const group = eventGroupState[projectState.selected];
                correctReadingSpeed(group, {
                    frameRate: projectState.frameRate,
                    maxCps,
                    minDuration,
                    maxDuration,
                    minFrameGap,
                    allowMerge,
                    maxChars: group.maxChars,
                    maxLines: group.maxLines,
                    excludeSpaces: metricsState.cpsIncludeSpaces === false,
                    markFailures: true,
                    failureNote: "Reading speed too high - could not be corrected automatically",
                });
            } catch (err) {
                console.log(err, err.message);
                toast.push(
                    "Failed to auto-correct reading speed.",
                    {
                        classes: ["toast-danger"],
                    },
                );
            }
        },
        fixEventOverlap(evgIndex) {
            try {
                const index = evgIndex ?? projectState.selected;
                if (!eventGroupState[index]) {
                    throw new Error(`Invalid event group index: ${index}`);
                }
                assertGroupEditable(index);
                eventGroupState[index] = fixOverlap(
                    eventGroupState[index],
                    editorState.minFrameGap,
                    projectState.frameRate,
                );
            } catch (err) {
                console.log(err, err.message);
                toast.push("Failed to fix event overlap.", {
                    classes: ["toast-danger"],
                });
            }
        },
        setProjectFrameGap(frameGap) {
            editorState.minFrameGap = frameGap;
        },
        loadProjectData(ccprj) {
            try {
                resetProjectState();
                const projectJson =
                    typeof ccprj === "string" ? JSON.parse(ccprj) : ccprj;

                // Update status
                updateStatus({
                    progress: 10,
                    msg: "Processing project data...",
                });

                // Create a new Project instance
                const newProject = {
                    ...new _Project({
                        ...projectJson,
                        ...{
                            eventGroups: [],
                            metadata: [],
                            speakers: [],
                            folderId: undefined,
                        },
                    }),
                };

                // Update projectState properties from newProject
                Object.keys(newProject).forEach((key) => {
                    if (
                        key !== "selected" ||
                        newProject.selected !== undefined
                    ) {
                        projectState[key] = newProject[key];
                    }
                });

                updateStatus({
                    progress: 30,
                    msg: "Loading event groups...",
                });

                // Process event groups
                if (
                    projectJson.eventGroups &&
                    Array.isArray(projectJson.eventGroups)
                ) {
                    projectJson.eventGroups.forEach(
                        (eventGroup, eventGroupIndex) => {
                            eventGroupState.push({
                                ...new _EventGroup({
                                    ...eventGroup,
                                    ...{
                                        events: [],
                                    },
                                }),
                            });

                            if (
                                eventGroup.events &&
                                Array.isArray(eventGroup.events)
                            ) {
                                eventGroup.events.forEach((event) => {
                                    eventGroupState[eventGroupIndex] =
                                        insertEvent(
                                            eventGroupState[eventGroupIndex],
                                            event,
                                        );
                                });
                            }
                        },
                    );
                }

                updateStatus({
                    progress: 50,
                    msg: "Loading project metadata...",
                });

                // Load metadata
                if (
                    projectJson.metadata &&
                    Array.isArray(projectJson.metadata)
                ) {
                    projectJson.metadata.forEach((metadataInfo) => {
                        metadataState.push(metadataInfo);
                    });
                }

                // Load speakers
                if (
                    projectJson.speakers &&
                    Array.isArray(projectJson.speakers)
                ) {
                    projectJson.speakers.forEach((speakerInfo) => {
                        speakerState.push(speakerInfo);
                    });
                }

                // Load issues
                if (projectJson.issues && Array.isArray(projectJson.issues)) {
                    projectJson.issues.forEach((issue) => {
                        issueState.push(issue);
                    });
                }

                // Load markers
                resetMarkerStateWithData(
                    projectJson.markers || {
                        selected: 0,
                        lists: [
                            {
                                id: "0",
                                name: "Shot Changes",
                                color: "#E74C3C",
                                markers: [],
                            },
                        ],
                    },
                );

                // Load style info
                if (projectJson.style) {
                    for (const [key, value] of Object.entries(
                        projectJson.style,
                    )) {
                        styleState[key] = value;
                    }
                }

                updateStatus({
                    progress: 70,
                    msg: "Setting up project...",
                });

                // Set selected event group if any exist
                if (eventGroupState.length > 0) {
                    projectState.selected = eventGroupState.length - 1;
                }

                setHistoryBaseline("load project");
                return {
                    success: true,
                    message: "Project loaded successfully.",
                };
            } catch (err) {
                console.error("Error loading project data:", err);
                toast.push("Failed to load project: " + err.message, {
                    classes: ["toast-danger"],
                });
                return { success: false, message: err.message };
            }
        },
        getHistory() {
            return {
                position: historyState.position,
                actions: historyState.actions.map(({ name, eventGroup }) => ({
                    name,
                    eventGroup,
                })),
            };
        },
        hasUnsavedChanges() {
            return checkUnsavedChanges();
        },
        getProjectData() {
            //Return the Project json.
            return ccprjFunc.createCcprj({
                project: JSON.parse(JSON.stringify(projectState)),
                metadata: JSON.parse(JSON.stringify(metadataState)),
                speakers: JSON.parse(JSON.stringify(speakerState)),
                issues: JSON.parse(JSON.stringify(issueState)),
                markers: JSON.parse(JSON.stringify(markerState)),
                eventGroups: JSON.parse(JSON.stringify(eventGroupState)),
                style: JSON.parse(JSON.stringify(styleState)),
            });
        },
        getSelectedEvents() {
            let selectedIndices =
                eventGroupState[projectState.selected].selected;
            let selectedEvents = selectedIndices.map(
                (index) => eventGroupState[projectState.selected].events[index],
            );
            return selectedEvents;
        },
        selectEventById(id) {
            let index = eventGroupState[projectState.selected].events.findIndex(
                (ev) => ev.id === id,
            );
            eventGroupState[projectState.selected].selected = [index];
        },
        selectEventByIndex(index) {
            /* Check if index is an array, or an integer */
            if (Array.isArray(index)) {
                eventGroupState[projectState.selected].selected = index;
            } else {
                eventGroupState[projectState.selected].selected = [index];
            }
        },
        scrollToEventById(id) {
            let index = eventGroupState[projectState.selected].events.findIndex(
                (ev) => ev.id === id,
            );

            if (index !== -1) {
                window.dispatchEvent(
                    new CustomEvent("scrollToVirtualListItem", {
                        detail: { index: index },
                    }),
                );
            }
        },
        scrollToEventByIndex(index) {
            window.dispatchEvent(
                new CustomEvent("scrollToVirtualListItem", {
                    detail: { index: index },
                }),
            );
        },
        /* Update Lock State */
        enableVideoLock() {
            lockState.video = true;
        },
        disableVideoLock() {
            lockState.video = false;
        },
        enableCaptionLock() {
            lockState.caption = true;
        },
        disableCaptionLock() {
            lockState.caption = false;
        },
        enablePreviewLock() {
            lockState.preview = true;
        },
        disablePreviewLock() {
            lockState.preview = false;
        },
    },
});

// Hand the resolved parent remote to the TTS bridge so Audio Description voice
// listing/rendering can call the parent's getVoices/renderAudio methods.
// Toolbar Close/Save visibility is controlled by environment.parentCancel /
// environment.parentExportSubtitle (envStore defaults or setParentToolbarActions).
connection.promise
    .then((remote) => {
        setParentRemote(remote);
    })
    .catch((err) => {
        console.error("Failed to establish parent PenPal connection:", err);
    });

function getSelectedEventGroup(validTypes) {
    let evGroup = eventGroupState[projectState.selected];
    if (evGroup) {
        if (validTypes.indexOf(evGroup.type) > -1) {
            return evGroup.id;
        } else {
            return false;
        }
    } else {
        return false;
    }
}

function resetProjectState() {
    projectState.selected = false;
    resetEventGroupState();
    speakerState.length = 0;
    issueState.length = 0;
    resetMarkerState();
    metadataState.length = 0;
    updateDuration(false);
    uiState.timeline = false;
}

async function updateStatus(statusInfo) {
    progress = statusInfo.progress;
    statusMsg = statusInfo.msg;
}
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'iFrameRemote' ? 'show d-block' : ''}"
    role="dialog"
    tabindex="-1"
    id="IFrameRemoteModal"
    aria-labelledby="iFrameRemoteTitle"
    aria-describedby="iFrameRemoteDesc">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 id="iFrameRemoteTitle" class="modal-title">
                    <img
                        id="systemLogoImg"
                        alt="System logo"
                        class="me-1"
                        src="./assets/img/logos/{environment.system}-icon.png" />
                    {modalTitle}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    onclick={hideModal}
                    id="closeIFrameRemote"></button>
            </div>
            <div class="modal-body" id="iFrameRemoteDesc">
                <p>
                    <i class="bi bi-exclamation-diamond-fill"></i>
                    {statusMsg}
                </p>
                <div class="progress">
                    <div
                        class="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style="width: {progress}%;"
                        aria-valuenow={progress}
                        aria-valuemin="0"
                        aria-valuemax="100">
                        {progress}%
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>

<style lang="css">
#systemLogoImg {
    height: 2rem;
    border-radius: 50%;
}
</style>
